import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';

import BalanceList, {
  formatBalanceAmount,
  pickDefaultBalanceRow,
  useCustomerBalanceRows,
  type BalanceRow,
} from '../account/BalanceList';
import { useSelection } from '../selection/SelectionContext';
import { brand } from '../theme';
import MintAvatar from '../ui/MintAvatar';

const DISPLAY_USD_KEY = 'cheddar-demo.displayBalancesInUsd';

const shimmer = {
  background: `linear-gradient(90deg,#F3ECE1 0%,${brand.canvasSunk} 40%,#F3ECE1 80%)`,
  backgroundSize: '320px 100%',
  animation: 'chd-shimmer 1.4s linear infinite',
} as const;

function loadDisplayInUsd(): boolean {
  try {
    return localStorage.getItem(DISPLAY_USD_KEY) === '1';
  } catch {
    return false;
  }
}

function saveDisplayInUsd(value: boolean): void {
  try {
    localStorage.setItem(DISPLAY_USD_KEY, value ? '1' : '0');
  } catch {
    // ignore quota / private mode
  }
}

function WalletIcon({ size = 20 }: { size?: number }) {
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      sx={{ display: 'block' }}
    >
      <path
        d="M3.5 7.75C3.5 6.231 4.731 5 6.25 5h11.5A2.75 2.75 0 0 1 20.5 7.75v.75h-1.75a.75.75 0 0 0 0 1.5H20.5v4.5h-1.75a.75.75 0 0 0 0 1.5H20.5v.75A2.75 2.75 0 0 1 17.75 19H6.25A2.75 2.75 0 0 1 3.5 16.25V7.75Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M3.5 9.5h17"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="16.75" cy="14" r="1" fill="currentColor" />
    </Box>
  );
}

function ChevronDown({ size = 16 }: { size?: number }) {
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      sx={{ display: 'block', flex: 'none' }}
    >
      <path
        d="M4 6.25 8 10.25 12 6.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

/**
 * After the first account+catalog load, pick the highest-USD mint (or the first
 * catalog mint when every balance is zero). Skip if the user already clicked a
 * row this session. Does not re-run on the 4s poll.
 */
function useDefaultMintSelection() {
  const { selection, selectMintById } = useSelection();
  const { balances, showSkeleton } = useCustomerBalanceRows();
  const didAutoPick = useRef(false);
  const userPicked = useRef(false);

  const markUserPicked = useCallback(() => {
    userPicked.current = true;
  }, []);

  useEffect(() => {
    if (didAutoPick.current || userPicked.current) return;
    if (showSkeleton || balances.length === 0) return;

    didAutoPick.current = true;
    const chosen = pickDefaultBalanceRow(balances, selection?.mintId);
    if (!chosen) return;
    const mintId = Number(chosen.id);
    if (!Number.isInteger(mintId) || mintId <= 0) return;
    if (selection?.mintId === mintId) return;
    selectMintById(mintId);
  }, [balances, showSkeleton, selection, selectMintById]);

  return { markUserPicked };
}

/** Header control: selected-currency chip + wallet icon open a mint picker. */
export default function WalletBalanceButton() {
  const { selection, selectMintById } = useSelection();
  const { balances, showSkeleton } = useCustomerBalanceRows();
  const { markUserPicked } = useDefaultMintSelection();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [displayInUsd, setDisplayInUsd] = useState(false);
  const open = Boolean(anchor);

  const selectedRow = useMemo(
    () =>
      selection
        ? (balances.find((row) => row.id === String(selection.mintId)) ?? null)
        : null,
    [balances, selection],
  );

  useEffect(() => {
    setDisplayInUsd(loadDisplayInUsd());
  }, []);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchor(wrapRef.current ?? event.currentTarget);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const handleToggleUsd = (_: unknown, checked: boolean) => {
    setDisplayInUsd(checked);
    saveDisplayInUsd(checked);
  };

  const handleSelect = (row: BalanceRow) => {
    markUserPicked();
    const mintId = Number(row.id);
    if (Number.isInteger(mintId) && mintId > 0) {
      selectMintById(mintId);
    }
    handleClose();
  };

  const chipLabel = selectedRow
    ? `${selectedRow.symbol} ${formatBalanceAmount(selectedRow, displayInUsd)}`
    : 'Select currency';
  return (
    <>
      <Box
        ref={wrapRef}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
      >
        <Box
          component="button"
          type="button"
          aria-label={chipLabel}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          data-testid="selected-currency-chip"
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            height: 36,
            px: 1,
            py: 0,
            border: `1px solid ${open ? brand.gold : brand.border}`,
            borderRadius: '10px',
            backgroundColor: open ? brand.goldWash : brand.surface,
            color: brand.ink,
            cursor: 'pointer',
            font: 'inherit',
            maxWidth: { xs: 168, sm: 220 },
            '&:hover': {
              backgroundColor: brand.goldWash,
              borderColor: brand.gold,
            },
          }}
        >
          {showSkeleton && !selectedRow ? (
            <Box
              sx={{
                width: 88,
                height: 16,
                borderRadius: '6px',
                ...shimmer,
              }}
            />
          ) : (
            <>
              <MintAvatar
                src={selectedRow?.image}
                symbol={selectedRow?.symbol ?? '?'}
                size={22}
              />
              <Box
                component="span"
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {selectedRow
                  ? formatBalanceAmount(selectedRow, displayInUsd)
                  : '—'}
              </Box>
              <ChevronDown />
            </>
          )}
        </Box>

        <IconButton
          aria-label="Your balances"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpen}
          size="small"
          sx={{
            color: brand.ink,
            border: `1px solid ${brand.border}`,
            borderRadius: '10px',
            width: 36,
            height: 36,
            backgroundColor: open ? brand.goldWash : brand.surface,
            '&:hover': { backgroundColor: brand.goldWash, borderColor: brand.gold },
          }}
        >
          <WalletIcon />
        </IconButton>
      </Box>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 300,
              maxWidth: 'calc(100vw - 24px)',
              p: 1.75,
              borderRadius: `${brand.cardRadius}px`,
              border: `1px solid ${brand.hairline}`,
              boxShadow: brand.cardShadow,
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: brand.faint,
            mb: 1.25,
          }}
        >
          Your balance
        </Typography>
        <Box
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            mx: -0.25,
            px: 0.25,
          }}
        >
          <BalanceList
            compact
            displayInUsd={displayInUsd}
            selectedMintId={selection?.mintId}
            onSelect={handleSelect}
          />
        </Box>
        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: `1px solid ${brand.hairline}`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={displayInUsd}
                onChange={handleToggleUsd}
                sx={{
                  color: brand.border,
                  '&.Mui-checked': { color: brand.goldDeep },
                }}
              />
            }
            label="Display in USD"
            sx={{
              m: 0,
              gap: 0.25,
              '& .MuiFormControlLabel-label': {
                fontSize: 13,
                fontWeight: 600,
                color: brand.body,
              },
            }}
          />
        </Box>
      </Popover>
    </>
  );
}
